<!--
  Per-field change timeline (Enhanced Audit Trail).
  Renders a small history icon beside a field; clicking opens a modal showing
  only that field's changes over time. Reuses the record history endpoint and
  filters client-side by fieldKey.

  Usage:
    <audit-field-timeline
      entity-type="Zone"
      :record-id="props.id"
      field-key="Colour"
      field-label="Colour" />
-->
<template>
  <span v-if="recordId" class="audit-field-timeline">
    <v-tooltip text="View change history" location="top">
      <template #activator="{ props: tip }">
        <v-btn
          v-bind="tip"
          icon
          size="x-small"
          variant="text"
          density="comfortable"
          class="ml-1"
          @click="openTimeline"
        >
          <v-icon size="16">mdi-history</v-icon>
        </v-btn>
      </template>
    </v-tooltip>

    <v-dialog v-model="open" max-width="640" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center">
          <span class="font-weight-bold">{{ fieldLabel }}</span>
          <v-spacer />
          <v-btn icon variant="text" @click="open = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text style="min-height: 140px">
          <div v-if="loading" class="d-flex justify-center pa-6">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <div v-else-if="error" class="text-error pa-4">{{ error }}</div>
          <div v-else-if="!timeline.length" class="text-medium-emphasis pa-4 text-center">
            No changes recorded for this field yet.
          </div>
          <v-table v-else density="compact" class="audit-table">
            <thead>
              <tr>
                <th class="text-left">DATE</th>
                <th class="text-left">CHANGED BY</th>
                <th class="text-left">OLD VALUE</th>
                <th class="text-left">NEW VALUE</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(t, i) in timeline" :key="i">
                <td class="text-no-wrap audit-date">{{ formatDate(t.changedDateTime) }}</td>
                <td>{{ t.changedBy }}</td>
                <td>{{ display(t.oldValue) }}</td>
                <td>{{ display(t.newValue) }}</td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </span>
</template>

<script setup>
import { ref } from "vue";
import { fetchAuditHistory } from "@/api/auditChanges";

const props = defineProps({
  entityType: { type: String, required: true },
  recordId: { type: [String, Number], default: null },
  fieldKey: { type: String, required: true },
  fieldLabel: { type: String, default: "Field" },
});

const open = ref(false);
const loading = ref(false);
const error = ref("");
const timeline = ref([]);

async function openTimeline() {
  open.value = true;
  loading.value = true;
  error.value = "";
  try {
    const history = await fetchAuditHistory(props.entityType, props.recordId);
    // Flatten to just this field's changes, newest first (history already sorted).
    const rows = [];
    for (const item of history) {
      const change = (item.changes || []).find(
        (c) => (c.fieldKey || "").toLowerCase() === props.fieldKey.toLowerCase()
      );
      if (change) {
        rows.push({
          changedDateTime: item.changedDateTime,
          changedBy: item.changedBy,
          oldValue: change.oldValue,
          newValue: change.newValue,
        });
      }
    }
    timeline.value = rows;
  } catch (e) {
    error.value = e?.response?.data?.message || e?.message || "Failed to load history.";
  } finally {
    loading.value = false;
  }
}

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("en-IE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function display(value) {
  return value === null || value === undefined || value === "" ? "—" : value;
}
</script>

<style scoped>
.audit-field-timeline {
  display: inline-flex;
  align-items: center;
}
.audit-table :deep(thead th) {
  background-color: #f0f1f3;
  font-weight: 600;
  color: #5a6472;
  letter-spacing: 0.02em;
}
.audit-table :deep(td.audit-date) {
  color: #0f8a8a;
}
</style>
